/* HAUNT — RoutineDarling
   - the shop carousel
   - phones: the exhibition page revolves under the fixed header and bottom menu
   - add to bag through Shopify's AJAX Cart API
   - the bag page, updated in place through the Section Rendering API */

(() => {
  'use strict';

  const HAUNT = window.HAUNT || {};
  const routes = Object.assign({ root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' }, HAUNT.routes);
  const t = (key) => (HAUNT.strings && HAUNT.strings[key]) || key;

  /* cart ------------------------------------------------------------------ */

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || t('error'));
    return data;
  }

  function setBagCount(count) {
    document.querySelectorAll('[data-bag-count]').forEach((el) => {
      el.textContent = count > 0 ? ` (${count})` : '';
    });
  }

  async function refreshBagCount() {
    try {
      setBagCount((await cartRequest(`${routes.cart}.js`)).item_count);
    } catch (_) {
      /* the count is a nicety; leave it as rendered */
    }
  }

  // back/forward cache can show a stale count
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refreshBagCount();
  });

  // a product without a bundled vector falls back to its Shopify image
  document.querySelectorAll('img[data-fallback]').forEach((img) => {
    const swap = () => {
      img.removeEventListener('error', swap);
      img.src = img.dataset.fallback;
    };
    if (img.complete && img.naturalWidth === 0 && img.src) swap();
    else img.addEventListener('error', swap);
  });

  /* shop carousel ----------------------------------------------------------- */

  // Geometry measured from the layouts. W is the centre shirt's width.
  //   desktop: W = 33.6% of the width (581px at 1728). A shirt p places out is scaled 1 − 0.1p and
  //            offset W·(0.5p − 0.05p²).
  //   phone:   W = 56.7% of the width (249px at 440). Neighbours drop to 0.75, sit 0.5W out and a
  //            touch lower; the details sit 0.51W below the shirt.
  // Shirt + details are placed between the wordmark and the menu as in the layouts.
  const SHIRT_RATIO = 311.7 / 334;
  const VISIBLE = 4; // shirts beyond this distance are hidden (they fade out from 3 to 4)

  function Shop(root) {
    const stage = root.querySelector('[data-stage]');
    const tees = Array.from(root.querySelectorAll('[data-tee]'));
    const shadows = tees.map((el) => el.querySelector('.tee__shadow'));
    const n = tees.length;
    if (!stage || !n) return;

    let products = [];
    try {
      products = JSON.parse(root.querySelector('[data-products]').textContent);
    } catch (_) {
      /* details stay as server-rendered */
    }

    const info = root.querySelector('[data-info]');
    const form = info && info.querySelector('[data-add-form]');
    const codeEl = info && info.querySelector('[data-code]');
    const titleEl = info && info.querySelector('[data-title]');
    const priceEl = info && info.querySelector('[data-price]');
    const label = info && info.querySelector('[data-size-label]');
    const sizes = info && info.querySelector('[data-sizes]');
    const addButton = info && info.querySelector('[data-add]');

    const phone = window.matchMedia('(max-width: 700px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const loop = n > 2 * VISIBLE;
    const mod = (i) => ((i % n) + n) % n;
    const wrap = (d) => (loop ? mod(d + n / 2) - n / 2 : d);
    const clampIndex = (i) => (loop ? i : Math.max(0, Math.min(n - 1, i)));
    // past either end of a short, non-looping carousel the drag resists
    const resist = (p) => (loop ? p : p < 0 ? p * 0.35 : p > n - 1 ? n - 1 + (p - n + 1) * 0.35 : p);

    let geo;
    let pos = clampIndex(parseInt(root.dataset.start, 10) || 0);
    let target = pos;
    let velocity = 0; // shirts per second
    let frame = 0;
    let lastTime = 0;
    let current = mod(pos);
    let detailsOn = root.hasAttribute('data-detail');
    const shopName = HAUNT.shopName || document.title;

    function measure() {
      const r = stage.getBoundingClientRect();
      const mobile = phone.matches;
      const title = document.querySelector('.site-title');
      const top = title ? title.getBoundingClientRect().bottom : 0.124 * r.height;
      if (mobile) {
        // The phone layout spaces wordmark, shirt, details and bottom menu with three equal gaps
        // (128px each at 440 × 956). Keep them equal on any screen; on short screens the shirt
        // shrinks rather than letting a gap drop below 24px.
        const bar = document.querySelector('.site-footer');
        const bottom = bar ? bar.getBoundingClientRect().top : r.height - 63;
        const band = bottom - top;
        const details = 109; // code, title, price (3 × 17) + 20 + sizes 17 + 4 + add to bag 17
        let W = 0.567 * r.width;
        let H = W * SHIRT_RATIO;
        let gap = (band - H - details) / 3;
        if (gap < 24) {
          gap = 24;
          H = Math.max(60, band - details - 3 * gap);
          W = H / SHIRT_RATIO;
        }
        geo = { mobile, W, H, cx: 0.5 * r.width, cy: top + gap + H / 2, a: 0.55 };
        geo.infoTop = top + gap + H + gap;
      } else {
        // Between the wordmark and the nav, shirt + details keep the layout's proportions:
        // 68% of the spare height above them, 32% below. Portrait screens let the shirt grow.
        const nav = document.querySelector('.site-nav--desktop');
        const bottom = nav ? nav.getBoundingClientRect().top : r.height - 66;
        const band = bottom - top;
        const laidOut = 62; // code, title and sizes, as in the layout
        const details = laidOut + 17; // plus the price line
        const aspect = r.width / r.height;
        const share = 0.336 + 0.164 * Math.max(0, Math.min(1, (1.5 - aspect) / 0.75));
        const W = Math.max(80, Math.min(share * r.width, 0.522 * r.height, (band - details - 70) / (SHIRT_RATIO + 0.1)));
        const H = W * SHIRT_RATIO;
        // the layout's position, unless that would leave less than 40px between the details and the nav
        const above = Math.min((band - (H + 0.1 * W + laidOut)) * 0.68, band - (H + 0.1 * W + details) - 40);
        geo = { mobile, W, H, cx: 0.5 * r.width, cy: top + above + H / 2, a: 0.5 };
        geo.infoTop = geo.cy + 0.5666 * W;
      }
      geo.step = geo.W * geo.a; // px of drag per shirt near the centre
      root.style.setProperty('--tee-w', `${geo.W}px`);
      root.style.setProperty('--cx', `${geo.cx}px`);
      root.style.setProperty('--cy', `${geo.cy}px`);
      root.style.setProperty('--info-top', `${geo.infoTop}px`);
    }

    // where a shirt d places from the centre sits
    function offset(d) {
      const c = Math.min(Math.abs(d), 5);
      return Math.sign(d) * geo.W * (geo.a * c - 0.05 * c * c);
    }

    function scaleAt(ad) {
      return geo.mobile ? 1 - 0.25 * Math.min(ad, 1) - 0.06 * Math.max(ad - 1, 0) : 1 - 0.1 * ad;
    }

    function lift(ad) {
      return geo.mobile ? 0.0265 * geo.W * Math.min(ad, 1) : 0;
    }

    function render() {
      for (let i = 0; i < n; i++) {
        const el = tees[i];
        const d = wrap(i - pos);
        const ad = Math.abs(d);
        if (ad > VISIBLE) {
          if (el.style.visibility !== 'hidden') el.style.visibility = 'hidden';
          continue;
        }
        const x = geo.cx + offset(d) - geo.W / 2;
        const y = geo.cy + lift(ad) - geo.H / 2;
        el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scaleAt(ad).toFixed(4)})`;
        el.style.zIndex = String(1000 - Math.round(ad * 100));
        el.style.opacity = String(Math.min(1, VISIBLE - ad));
        el.style.visibility = 'visible';
        shadows[i].style.opacity = (0.75 * Math.max(0, 1 - ad * 1.25)).toFixed(3);
      }
    }

    // critically damped spring towards `target`, carrying the release velocity of a swipe
    function tick(now) {
      const dt = Math.min(0.05, (now - lastTime) / 1000 || 0.016);
      lastTime = now;
      const k = 170;
      velocity += (k * (target - pos) - 2 * Math.sqrt(k) * velocity) * dt;
      pos += velocity * dt;
      if (Math.abs(target - pos) < 0.0005 && Math.abs(velocity) < 0.01) {
        pos = target;
        velocity = 0;
        frame = 0;
        render();
        settle();
        return;
      }
      render();
      frame = requestAnimationFrame(tick);
    }

    function animate() {
      if (reducedMotion.matches) {
        pos = target;
        velocity = 0;
        render();
        settle();
        return;
      }
      if (!frame) {
        lastTime = performance.now();
        frame = requestAnimationFrame(tick);
      }
    }

    function stop() {
      cancelAnimationFrame(frame);
      frame = 0;
      velocity = 0;
    }

    function goTo(index) {
      detailsOn = true;
      target = clampIndex(Math.round(index));
      hideDetails();
      animate();
    }

    function settle() {
      if (loop) pos = target = mod(target);
      const index = mod(target);
      tees.forEach((el, i) => el.classList.toggle('is-current', i === index));
      if (index !== current) {
        current = index;
        fillDetails(products[index]);
      }
      if (detailsOn) {
        showDetails();
        syncUrl(products[index]);
      }
    }

    /* details */

    function showDetails() {
      if (info) info.classList.add('is-visible');
    }

    function hideDetails() {
      if (info) info.classList.remove('is-visible');
    }

    function fillDetails(product) {
      if (!info || !product) return;
      codeEl.textContent = product.code || '';
      codeEl.hidden = !product.code;
      titleEl.textContent = product.title;
      priceEl.textContent = product.price;
      form.querySelectorAll('[data-default-variant]').forEach((el) => el.remove());
      sizes.textContent = '';
      label.hidden = product.defaultVariant;
      if (product.defaultVariant) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        input.value = product.variants[0].id;
        input.setAttribute('data-default-variant', '');
        form.prepend(input);
      } else {
        product.variants.forEach((variant) => {
          const option = document.createElement('label');
          option.className = 'info__size';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'id';
          input.value = variant.id;
          input.required = true;
          input.disabled = !variant.available;
          const text = document.createElement('span');
          text.textContent = variant.title;
          option.append(input, text);
          sizes.append(option);
        });
      }
      const available = product.variants.some((v) => v.available);
      addButton.disabled = !available;
      addButton.textContent = available ? t('addToBag') : t('soldOut');
    }

    function syncUrl(product) {
      if (!product || !product.url) return;
      if (window.location.pathname !== product.url) {
        window.history.replaceState(window.history.state, '', product.url);
      }
      document.title = `${product.title} — ${shopName}`;
    }

    if (form) {
      form.noValidate = true; // we prompt for a size ourselves
      form.addEventListener('change', (e) => {
        if (e.target.name !== 'id') return;
        const product = products[current];
        const variant = product && product.variants.find((v) => String(v.id) === e.target.value);
        if (variant && priceEl) priceEl.textContent = variant.price;
      });
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const chosen = form.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
        if (!chosen) {
          label.classList.remove('is-prompting');
          void label.offsetWidth; // restart the blink
          label.classList.add('is-prompting');
          return;
        }
        addButton.disabled = true;
        addButton.textContent = t('adding');
        const restore = (delay) =>
          setTimeout(() => {
            addButton.textContent = t('addToBag');
            addButton.disabled = false;
          }, delay);
        try {
          await cartRequest(`${routes.cartAdd}.js`, { items: [{ id: Number(chosen.value), quantity: 1 }] });
          addButton.textContent = t('added');
          refreshBagCount();
          restore(2000);
        } catch (err) {
          addButton.textContent = err.message.toLowerCase();
          restore(2600);
        }
      });
    }

    /* input: drag / swipe */

    // which shirt is under the pointer: nearest the centre first, against a rough T silhouette
    function hitTest(x, y) {
      const candidates = [];
      for (let i = 0; i < n; i++) {
        const d = wrap(i - pos);
        if (Math.abs(d) <= VISIBLE - 0.5) candidates.push([Math.abs(d), i, d]);
      }
      candidates.sort((a, b) => a[0] - b[0]);
      for (const [ad, i, d] of candidates) {
        const s = scaleAt(ad);
        const cx = geo.cx + offset(d);
        const cy = geo.cy + lift(ad);
        const u = (x - cx) / (geo.W * s) + 0.5;
        const v = (y - cy) / (geo.H * s) + 0.5;
        if (u < 0 || u > 1 || v < 0 || v > 1) continue;
        const body = u >= 0.23 && u <= 0.77 && v >= 0.02;
        const sleeves = v >= 0.1 && v <= 0.52;
        if (body || sleeves) return i;
      }
      return -1;
    }

    function tap(x, y) {
      const i = hitTest(x, y);
      if (i < 0) return;
      const d = Math.round(wrap(i - target));
      if (d === 0) {
        detailsOn = true;
        settle();
      } else {
        goTo(target + d);
      }
    }

    let drag = null;

    stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || drag) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, from: pos, moved: false, samples: [] };
      stage.setPointerCapture(e.pointerId);
    });

    stage.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const delta = e.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(delta) < 6) return;
        drag.moved = true;
        drag.from = pos;
        drag.origin = delta;
        stop();
        hideDetails();
        stage.classList.add('is-dragging');
      }
      pos = resist(drag.from - (delta - drag.origin) / geo.step);
      drag.samples.push([e.timeStamp, pos]);
      if (drag.samples.length > 8) drag.samples.shift();
      render();
    });

    function release(e) {
      if (!drag || e.pointerId !== drag.id) return;
      const ended = drag;
      drag = null;
      stage.classList.remove('is-dragging');
      if (!ended.moved) {
        if (e.type === 'pointerup') tap(e.clientX, e.clientY);
        return;
      }
      const recent = ended.samples.filter(([time]) => e.timeStamp - time < 120);
      let v = 0;
      if (recent.length > 1) {
        const [t0, p0] = recent[0];
        const [t1, p1] = recent[recent.length - 1];
        v = ((p1 - p0) / Math.max(8, t1 - t0)) * 1000;
      }
      // a flick carries on a little, never more than four shirts past where it was let go
      velocity = Math.max(-25, Math.min(25, v));
      const projected = pos + velocity * 0.12;
      detailsOn = true;
      target = clampIndex(Math.round(Math.max(pos - 4, Math.min(pos + 4, projected))));
      animate();
    }

    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('lostpointercapture', release);
    stage.addEventListener('click', (e) => e.preventDefault()); // shirts are links only without JS
    stage.addEventListener('dragstart', (e) => e.preventDefault());

    /* input: trackpad / mouse wheel */

    let wheelTimer = 0;
    window.addEventListener(
      'wheel',
      (e) => {
        if (e.ctrlKey) return; // pinch zoom
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
        const dx = e.deltaX * unit;
        const dy = e.deltaY * unit;
        const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
        if (!delta) return;
        e.preventDefault();
        stop();
        hideDetails();
        detailsOn = true;
        pos = resist(pos + delta / geo.step);
        render();
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
          target = clampIndex(Math.round(pos));
          animate();
        }, 140);
      },
      { passive: false }
    );

    /* input: keyboard */

    document.addEventListener('keydown', (e) => {
      const inShop = e.target === document.body || e.target === stage || stage.contains(e.target);
      if (!inShop || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goTo(target + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goTo(target - 1);
      } else if ((e.key === 'Enter' || e.key === ' ') && e.target === stage) {
        e.preventDefault();
        detailsOn = true;
        settle();
        const first = info && info.querySelector('input:not([type="hidden"]):not(:disabled), button');
        if (first) first.focus();
      }
    });

    /* layout */

    const relayout = () => {
      measure();
      render();
    };
    window.addEventListener('resize', relayout);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', relayout);
    phone.addEventListener('change', relayout);

    measure();
    render();
    root.classList.add('is-ready');
    tees[current].classList.add('is-current');

    // Load every shirt once the page has settled, so swiping never reveals an empty one.
    const loadAll = () =>
      setTimeout(() => {
        root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }, 300);
    if (document.readyState === 'complete') loadAll();
    else window.addEventListener('load', loadAll, { once: true });
  }

  /* bag page ---------------------------------------------------------------- */

  function Bag(root) {
    root.addEventListener('click', async (e) => {
      const link = e.target.closest('a[data-change]');
      if (!link) return;
      e.preventDefault();
      const item = link.closest('[data-key]');
      root.classList.add('is-busy');
      try {
        const cart = await cartRequest(`${routes.cartChange}.js`, {
          id: item.dataset.key,
          quantity: Number(link.dataset.change),
        });
        setBagCount(cart.item_count);
        const res = await fetch(`${routes.cart}?section_id=${encodeURIComponent(root.dataset.sectionId)}`);
        const fresh = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-bag]');
        if (!fresh) throw new Error('no section');
        root.replaceWith(fresh);
        Bag(fresh);
      } catch (_) {
        window.location.href = link.href; // the plain link still works
      }
    });
  }

  /* phones: the exhibition page revolves -------------------------------------
     The page is repeated twice more below the original, under the fixed header. The visitor starts
     in the middle copy; whenever scrolling comes to rest more than half a copy away from it, the
     page moves back by exactly one copy. Every copy looks the same, so the move can't be seen and
     the page feels like it goes round: past the last photograph comes the beginning again. */

  const phone = window.matchMedia('(max-width: 700px)');

  function Revolve() {
    const main = document.getElementById('main');
    if (!main) return;
    const copies = [];
    let period = 0;
    let timer = 0;

    const measure = () => {
      period = copies.length ? copies[0].getBoundingClientRect().top - main.getBoundingClientRect().top : 0;
    };

    const recentre = () => {
      if (!period) return;
      const y = window.scrollY;
      if (y < period * 0.5) window.scrollTo(0, y + period);
      else if (y > period * 1.5) window.scrollTo(0, y - period);
    };
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(recentre, 140);
    };

    function start() {
      let anchor = main;
      for (let k = 0; k < 2; k++) {
        const copy = main.cloneNode(true);
        copy.removeAttribute('id');
        copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        copy.setAttribute('aria-hidden', 'true');
        anchor.after(copy);
        anchor = copy;
        copies.push(copy);
      }
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      measure();
      window.scrollTo(0, period + window.scrollY);
      if ('onscrollend' in window) window.addEventListener('scrollend', recentre);
      else window.addEventListener('scroll', onScroll, { passive: true });
    }

    function stop() {
      const y = period ? window.scrollY % period : window.scrollY;
      copies.splice(0).forEach((copy) => copy.remove());
      window.removeEventListener('scrollend', recentre);
      window.removeEventListener('scroll', onScroll);
      period = 0;
      window.scrollTo(0, y);
    }

    if (phone.matches) start();
    phone.addEventListener('change', () => (phone.matches ? start() : stop()));
    window.addEventListener('resize', () => {
      if (!period) return;
      const ratio = window.scrollY / period;
      measure();
      window.scrollTo(0, ratio * period);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  document.querySelectorAll('[data-shop]').forEach(Shop);
  document.querySelectorAll('[data-bag]').forEach(Bag);
  if (document.querySelector('[data-exhibition]')) Revolve();
})();
